/* eslint-disable no-underscore-dangle */
import Axios, { AxiosResponse } from 'axios'
import getServerConfig from 'data/serverConfig'
import { AdminUser } from 'types'
import localStorage from 'utils/localStorage'

/**
 * BaseURL is gotten from ENV variables during build time
 */

export const baseURL = getServerConfig(
	localStorage.get('platform') || 'DASH_CX'
).url

/**
 * Type of RefreshToken Response
 */
export interface RefreshResponseData {
	user: AdminUser
}
declare global {
	// eslint-disable-next-line no-unused-vars
	interface Window {
		refreshTokenPromise: null | Promise<AxiosResponse<RefreshResponseData, any>>
	}
}

if (typeof window !== 'undefined') {
	window.refreshTokenPromise = window.refreshTokenPromise || null
}

/**
 * Creating a new Axios instance with the baseURL and the interceptors
 */

const axios = Axios.create({ baseURL })

/**
 * Axios interceptor for adding the Authorization header to every request.
 *
 * This gets the token from localStorage and adds it to the Authorization header.
 */

axios.interceptors.request.use(
	async config => {
		if (window.refreshTokenPromise !== null) {
			try {
				await window.refreshTokenPromise
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error)
			}
		}
		const token = localStorage.get('token')

		if (token) {
			// eslint-disable-next-line no-param-reassign
			config.headers = { ...config.headers, authorization: `BEARER ${token}` }
		}
		return config
	},
	error => Promise.reject(error)
)

/**
 * Function deletes the session and redirects to the login page.
 *
 * This function dispatches the sessionExpired event window.
 *
 * This event is handled by the Main Router component. Location: 'src/Router/index.tsx'
 */

function deleteSession() {
	const event = new Event('sessionExpired')
	window.dispatchEvent(event)
}

/**
 * Axios Interceptor for handling 401 errors and refreshing the token
 *
 * This interceptor handles 401 errors and refreshes the token if possible.
 *
 * If the token is refreshed, the request is retried.
 *
 * If the token is not refreshed, the session is deleted and the user is redirected to the login page.
 */

axios.interceptors.response.use(
	res => {
		return res
	},
	async err => {
		const originalConfig = err.config
		if (
			originalConfig.url !== 'management/login' &&
			originalConfig.url !== 'management/refresh-login' &&
			originalConfig.url !== 'management/login/two-factor' &&
			err.response
		) {
			if (err.response.status === 401 && !originalConfig._retry) {
				originalConfig._retry = true

				try {
					const refreshToken = localStorage.get('refreshToken')
					if (refreshToken) {
						if (window.refreshTokenPromise === null) {
							window.refreshTokenPromise = Axios.get<RefreshResponseData>(
								`${baseURL}management/refresh-login`,
								{
									headers: {
										Authorization: `BEARER ${refreshToken}`,
									},
								}
							)
						}

						if (window.refreshTokenPromise !== null) {
							const { data } = await window.refreshTokenPromise
							localStorage.set('token', data.user.token)
							localStorage.set('refreshToken', data.user.refreshToken)
							window.refreshTokenPromise = null
							return axios(originalConfig)
						}
					}
					deleteSession()
				} catch (_error) {
					deleteSession()
					window.refreshTokenPromise = null
					return Promise.reject(_error)
				}
			}
		}
		return Promise.reject(err)
	}
)

export default axios
